import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';
import { NgxMaskModule } from 'ngx-mask';
import { ModalModule } from 'ngx-bootstrap/modal';

import pack from 'package.json';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { STORAGE_KEY } from '@coin-depo-core/storage';
import { COUNTRIES_API } from '@coin-depo-core/countries';
import { FOUNDS_API } from '@coin-depo-core/founds';
import { AUTH_SERVICE_TOKEN } from '@coin-depo-core/auth';

import { APP_CONFIG } from '@coin-depo-config/tokens';

import {
  AuthService,
  AccountStatsService,
  BalancesService,
  CoinsService,
  TokenStatsService,
  VerificationService,
  AdvertisementService,
  AdminCustomerAccountServiceStubFacade,
  BorrowStatsService,
  DepositAssetsService,
  TransactionsService,
  GovernanceService,
} from '@coin-depo/services';

import { ApiService } from '@coin-depo/http';

import { TOKEN_API, TOKEN_FACADE, TokenService } from '@coin-depo-core/token';
import { BALANCES_FACADE } from '@coin-depo-core/balances';

import { environment } from '@coin-depo-config/environments/core-env/environment';
import { provideI18n } from '@coin-depo-i18n';

import { COIN_FACADE } from '@coin-depo-core/coin';

import { DEPOSIT_ASSETS_FACADE } from '@coin-depo-core/deposit-assets';

import { TRANSACTIONS_FACADE } from '@coin-depo-core/transactions';

import { ACCOUNT_STATS_FACADE } from '@coin-depo-core/account-stats';
import { TOKEN_STATS_FACADE } from '@coin-depo-core/token-stats';

import { BORROW_STATS_FACADE } from '@coin-depo-core/borrow-stats';
import { ADVERTISEMENT_FACADE } from '@coin-depo-core/advertisement';

import { ADMIN_CUSTOMER_ACCOUNT_FACADE } from '@coin-depo-core/admin-customer-account';

import { VERIFICATION_FACADE } from '@coin-depo-core/verification';

import { GOVERNANCE_FACADE } from '@coin-depo-core/governance';

import * as Sentry from "@sentry/angular";
import { Router } from '@angular/router';

console.log(`%c Version: ${pack.version}`, 'color: blue');

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ModalModule.forRoot(),
    ToastrModule.forRoot({
      enableHtml: true,
      positionClass: 'toast-top-right',
      toastClass: 'cd-toast',
      closeButton: true,
      timeOut: 4000,
    }),
    AppRoutingModule,
    NgxMaskModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      useDefaultLang: false,
    }),
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideI18n({
      type: 'dashboard',
      crowdinId: '1942',
      env: environment.env,
    }),
    {
      provide: APP_CONFIG,
      useValue: {
        isAdmin: false,
        appName: 'user',
        loginUrl: '/auth/login',
        afterLoginUrl: '/'
      }
    },
    {
      provide: STORAGE_KEY,
      useValue: 'config_storage',
    },
    {
      provide: AUTH_SERVICE_TOKEN,
      useClass: AuthService,
    },
    {
      provide: COUNTRIES_API,
      useExisting: ApiService
    },
    {
      provide: FOUNDS_API,
      useExisting: ApiService
    },
    {
      provide: TOKEN_API,
      useExisting: ApiService
    },
    {
      provide: COIN_FACADE,
      useClass: CoinsService
    },
    {
      provide: TOKEN_FACADE,
      useClass: TokenService
    },
    {
      provide: DEPOSIT_ASSETS_FACADE,
      useClass: DepositAssetsService
    },
    {
      provide: TRANSACTIONS_FACADE,
      useClass: TransactionsService,
    },
    {
      provide: BALANCES_FACADE,
      useClass: BalancesService
    },
    {
      provide: ACCOUNT_STATS_FACADE,
      useClass: AccountStatsService
    },
    {
      provide: TOKEN_STATS_FACADE,
      useClass: TokenStatsService
    },
    {
      provide: BORROW_STATS_FACADE,
      useClass: BorrowStatsService
    },
    {
      provide: ADVERTISEMENT_FACADE,
      useClass: AdvertisementService
    },
    {
      provide: VERIFICATION_FACADE,
      useClass: VerificationService
    },
    {
      provide: ADMIN_CUSTOMER_ACCOUNT_FACADE,
      useClass: AdminCustomerAccountServiceStubFacade
    },
    {
      provide: GOVERNANCE_FACADE,
      useClass: GovernanceService
    }
  ],
})
export class AppModule {}
